import { toDate } from "date-fns-tz";
import { TFunction } from "i18next";

import { Granularity } from "shared/api/constants";
import { EventsByAssetAge } from "shared/api/schemaAnalytics/types";
import { SchemaDefinition } from "shared/api/schemaDefinitions/types";
import { DataType, PopulationLine } from "shared/types";

import { NONE_EXPOSURE_ID } from "pages/shared/analytics/constants";

import { GRAPH_STROKE_WIDTH } from "features/ui/charts/constants";
import { DataElement } from "features/ui/charts/types";
import { getColor } from "features/ui/charts/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

import { ByAssetAgeOptions } from "./types";

type RowData = Record<string, string | number | null | undefined>;

const toTimestamp = (dateString: string): number =>
  toDate(dateString).getTime();

export const generateByAssetAgeYAxisLines = (
  data: EventsByAssetAge[],
  byAssetAgeData: ByAssetAgeOptions,
  reverseOrder: boolean = false
): PopulationLine[] => {
  const { yAxisKey, yAxisValue, byAssetAgeBirthday, byAssetAgeExposure } =
    byAssetAgeData;
  const uniqueIDs = Array.from(
    new Set(data.map(({ exposureBucket }) => exposureBucket))
  ).sort((a, b) => (reverseOrder ? b - a : a - b));

  return uniqueIDs.map((exposureBucket: number, index: number) => ({
    color: getColor(index),
    label:
      byAssetAgeExposure !== NONE_EXPOSURE_ID
        ? exposureBucket.toString()
        : yAxisValue,
    dashed: false,
    key: `${yAxisKey}-${byAssetAgeBirthday}-${exposureBucket}`,
    width: GRAPH_STROKE_WIDTH,
    dot: true,
  }));
};

export const prepareByAssetAgeDataByTimestamp = (
  data: EventsByAssetAge[] | undefined,
  byAssetAgeData: ByAssetAgeOptions
): DataElement[] => {
  if (!data) {
    return [];
  }

  const { yAxisKey, byAssetAgeBirthday } = byAssetAgeData;

  return data
    .map((item) => {
      const { birthdayBucket, exposureBucket, ...otherData } = item;
      const keyYAxis = `${yAxisKey}-${byAssetAgeBirthday}-${exposureBucket}`;

      const ts = toTimestamp(birthdayBucket);

      return {
        [keyYAxis]: item[yAxisKey],
        ts,
        exposureBucket,
        [exposureBucket]: otherData,
        byAssetAgeData,
      };
    })
    .reduce((acc: DataElement[], value: DataElement) => {
      const i = acc.findIndex(({ ts }) => ts === value.ts);
      if (i > -1) {
        acc[i] = { ...acc[i], ...value };
      } else {
        acc.push(value);
      }

      return acc;
    }, []);
};

export const prepareAssetsByAgeTableSchemaAndData = (
  data: EventsByAssetAge[],
  granularity: Granularity,
  buildDateLabel: string,
  exposure: string,
  t: TFunction
) => {
  const assetLabel = t("common:event_type.asset_other");
  const newData: RowData[] = [];
  const exposureList: number[] = [];

  data.forEach(
    ({
      birthdayBucket,
      cumulativeIPTA,
      distinctIPTA,
      numAssets,
      numEvents,
      exposureBucket,
      assetsAtExposure,
      percentAssetsAtExposure,
      totalAssets,
      FRCPercent,
    }) => {
      exposureList.push(exposureBucket);

      const currentRow = newData.find(
        (row) => row.birthdayBucket === birthdayBucket
      );

      if (currentRow) {
        currentRow[`FRCPercent_${exposureBucket}`] = FRCPercent;
        currentRow[`IPTA_${exposureBucket}`] = cumulativeIPTA;
        currentRow[`DIPTA_${exposureBucket}`] = distinctIPTA;
        currentRow[`numEvents_${exposureBucket}`] = numEvents;
        currentRow[`numAssets_${exposureBucket}`] = numAssets;
        currentRow[`assetsAtExposure_${exposureBucket}`] = assetsAtExposure;
        currentRow[`totalAssets_${exposureBucket}`] = totalAssets;
        currentRow[`percentAssetsAtExposure_${exposureBucket}`] =
          percentAssetsAtExposure;
      } else {
        newData.push({
          birthdayBucket,
          [`FRCPercent_${exposureBucket}`]: FRCPercent,
          [`IPTA_${exposureBucket}`]: cumulativeIPTA,
          [`DIPTA_${exposureBucket}`]: distinctIPTA,
          [`numEvents_${exposureBucket}`]: numEvents,
          [`numAssets_${exposureBucket}`]: numAssets,
          [`assetsAtExposure_${exposureBucket}`]: assetsAtExposure,
          [`totalAssets_${exposureBucket}`]: totalAssets,
          [`percentAssetsAtExposure_${exposureBucket}`]:
            percentAssetsAtExposure,
        });
      }
    }
  );

  const uniqueExposureValues = new Set(exposureList);
  const exposureValuesArray = Array.from(uniqueExposureValues).sort(
    (a, b) => a - b
  );

  const skipExposureColumns = exposure === NONE_EXPOSURE_ID;

  const exposureSchemaEntries = exposureValuesArray
    .map((exposureBucket) =>
      [
        {
          accessor: `FRCPercent_${exposureBucket}`,
          label: t("common:analytics.by_age.frc_label"),
          dataType: DataType.PERCENTAGE,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `IPTA_${exposureBucket}`,
          label: t("common:analytics.by_age.estimated_events_assets_label", {
            asset: assetLabel,
          }),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `DIPTA_${exposureBucket}`,
          label: t("common:analytics.by_age.estimated_affected_assets_label", {
            asset: assetLabel,
          }),
          dataType: DataType.NUMBER,
          decimals: 2,
          minDecimals: 2,
        },
        {
          accessor: `numEvents_${exposureBucket}`,
          label: t("common:analytics.by_age.incident_count_label"),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `numAssets_${exposureBucket}`,
          label: t("common:analytics.by_age.affected_assets_label", {
            asset: assetLabel,
          }),
          dataType: DataType.NUMBER,
        },
        !skipExposureColumns && {
          accessor: `assetsAtExposure_${exposureBucket}`,
          label: t("common:analytics.by_age.assets_at_mis_label", {
            asset: assetLabel,
          }),
          dataType: DataType.NUMBER,
        },
        {
          accessor: `totalAssets_${exposureBucket}`,
          label: t("common:analytics.by_age.assets_manufactured_label", {
            asset: assetLabel,
          }),
          dataType: DataType.NUMBER,
        },
        !skipExposureColumns && {
          accessor: `percentAssetsAtExposure_${exposureBucket}`,
          label: t("common:analytics.by_age.assets_at_mis_percent_label", {
            asset: assetLabel,
          }),
          dataType: DataType.NUMBER,
        },
      ].filter(Boolean)
    )
    .flat() as SchemaEntry[];

  const schema: SchemaEntry[] = [
    {
      accessor: "birthdayBucket",
      label: buildDateLabel,
      dataType:
        granularity === Granularity.MONTH
          ? DataType.DATE_YEAR_MONTH
          : DataType.DATE_YEAR_MONTH_DAY,
    },
    ...exposureSchemaEntries,
  ];

  return {
    data: newData,
    schema,
    uniqueExposureValues,
  };
};

export const isSchemaAnalyticsConfigured = (schema: SchemaDefinition) => {
  const analyticsConfig = schema.config?.analyticsConfig;

  return (
    analyticsConfig &&
    analyticsConfig.assetName &&
    analyticsConfig.dateField &&
    analyticsConfig.assetBirthdayField
  );
};
